/** Объединения варок — порт merge-логики оригинала (build-скрипт): parseTags (L6577),
 * нормализаторы (L6658-6685), canMergeRows (L6622), окно кандидатов ±4 недели от
 * текущей (L6653), группы = компоненты связности графа (L7725), целевая неделя = мода. */
import type { ArticleAttrs } from "@/api/orders-planning";
import { productionWeekIndex } from "./production";

/** Порт parseTags: разделители #,;\n → массив с префиксом #. */
export function parseTags(value: string): string[] {
  return value
    .replace(/\r?\n/g, ",")
    .split(/[#,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => `#${t}`);
}

/** Порт stripMergeJoinWords: lower, ё→е, &+ и кавычки → пробел, служебные слова прочь. */
export function stripMergeJoinWords(value: string): string {
  return value
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[&+]/g, " ")
    .replace(/[«»"']/g, " ")
    .split(/\s+/)
    .filter((w) => w && !["и", "and", "with", "c", "со", "на", "для"].includes(w))
    .join(" ");
}

/** Порт normalizeProductionText: токены отсортированы и склеены. */
export function normalizeProductionText(value: string): string {
  return stripMergeJoinWords(value)
    .replace(/[^а-яa-z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .sort()
    .join("");
}

/** Порт normalizeCompactText: только буквы/цифры. */
export function normalizeCompactText(value: string): string {
  return stripMergeJoinWords(value).replace(/[^а-яa-z0-9]/g, "");
}

/** Порт normalizeSemiFinished: токен-ключ + компакт + анаграммный ключ. */
export function normalizeSemiFinished(value: string): string[] {
  const token = normalizeProductionText(value);
  const compact = normalizeCompactText(value);
  const char = compact.split("").sort().join("");
  return [token, compact, char];
}

function tagSet(value: string, normalizer: (v: string) => string | string[]): Set<string> {
  const set = new Set<string>();
  for (const tag of parseTags(value)) {
    const raw = tag.slice(1);
    const result = normalizer(raw);
    if (Array.isArray(result)) {
      for (const r of result) if (r) set.add(r);
    } else if (result) {
      set.add(result);
    }
  }
  return set;
}

export interface MergeData {
  productTags: Set<string>;
  semiTags: Set<string>;
  hasTags: boolean;
  displayTags: string[];
}

/** Порт productionMergeData. */
export function mergeData(attrs: ArticleAttrs | undefined): MergeData {
  const productRaw = attrs?.merge_product ?? "";
  const semiRaw = attrs?.semi_finished ?? "";
  const productTags = tagSet(productRaw, normalizeProductionText);
  const semiTags = tagSet(semiRaw, normalizeSemiFinished);
  const seen = new Set<string>();
  const displayTags: string[] = [];
  for (const tag of [...parseTags(productRaw), ...parseTags(semiRaw)]) {
    const key = normalizeCompactText(tag.slice(1));
    if (key && !seen.has(key) && displayTags.length < 6) {
      seen.add(key);
      displayTags.push(tag);
    }
  }
  return { productTags, semiTags, hasTags: productTags.size > 0 || semiTags.size > 0, displayTags };
}

function intersects(a: Set<string>, b: Set<string>): boolean {
  for (const x of a) if (b.has(x)) return true;
  return false;
}

/** Порт canMergeRows: хотя бы одна категория заполнена у обоих И совпала; вторая,
 * если заполнена у обоих, не должна противоречить. */
export function canMerge(a: MergeData, b: MergeData, sameArticle: boolean): boolean {
  if (sameArticle) return false;
  if (!a.hasTags || !b.hasTags) return false;
  const productBoth = a.productTags.size > 0 && b.productTags.size > 0;
  const semiBoth = a.semiTags.size > 0 && b.semiTags.size > 0;
  const productOk = productBoth ? intersects(a.productTags, b.productTags) : true;
  const semiOk = semiBoth ? intersects(a.semiTags, b.semiTags) : true;
  return productOk && semiOk && ((productBoth && productOk) || (semiBoth && semiOk));
}

export interface MergeItem {
  article: string;
  name: string;
  weekKey: string;
  weekIndex: number;
  qty: number;
  liters: number;
  data: MergeData;
  confirmed: boolean;
}

export interface MergeGroup {
  items: MergeItem[];
  targetWeekIndex: number;
  totalQty: number;
  totalLiters: number;
  weekCount: number;
  isMerged: boolean;
  isPartlyMerged: boolean;
  confirmed: boolean;
  tags: string[];
}

export interface MergeStats {
  minWeek: number;
  taggedTotal: number;
  futureTagged: number;
  pastTagged: number;
}

/** Порт productionMergeGroups: планируемые непроизведённые позиции с тегами от текущей
 * недели; ребро при |Δнедель| ≤ 4 и canMerge; компоненты связности ≥ 2; целевая
 * неделя — мода (при равенстве — меньшая). */
export function mergeGroups(
  items: MergeItem[],
  currentWeek: number,
): { groups: MergeGroup[]; stats: MergeStats } {
  const tagged = items.filter((i) => i.data.hasTags && i.qty > 0);
  const future = tagged.filter((i) => i.weekIndex >= currentWeek);
  const past = tagged.length - future.length;

  // граф на future
  const n = future.length;
  const adj: number[][] = Array.from({ length: n }, () => []);
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      const a = future[i]!;
      const b = future[j]!;
      if (Math.abs(a.weekIndex - b.weekIndex) <= 4 && canMerge(a.data, b.data, a.article === b.article)) {
        adj[i]!.push(j);
        adj[j]!.push(i);
      }
    }
  }
  const visited = new Array<boolean>(n).fill(false);
  const groups: MergeGroup[] = [];
  for (let start = 0; start < n; start += 1) {
    if (visited[start]) continue;
    const stack = [start];
    visited[start] = true;
    const component: number[] = [];
    while (stack.length) {
      const v = stack.pop()!;
      component.push(v);
      for (const w of adj[v]!) {
        if (!visited[w]) {
          visited[w] = true;
          stack.push(w);
        }
      }
    }
    if (component.length < 2) continue;
    const items2 = component
      .map((i) => future[i]!)
      .sort((a, b) => a.weekIndex - b.weekIndex || a.article.localeCompare(b.article, "ru"));
    // мода недель; при равенстве — меньший индекс
    const counts = new Map<number, number>();
    for (const it of items2) counts.set(it.weekIndex, (counts.get(it.weekIndex) ?? 0) + 1);
    let targetWeekIndex = items2[0]!.weekIndex;
    let best = 0;
    for (const [week, count] of [...counts.entries()].sort((a, b) => a[0] - b[0])) {
      if (count > best) {
        best = count;
        targetWeekIndex = week;
      }
    }
    const totalQty = items2.reduce((s, i) => s + i.qty, 0);
    const totalLiters = Math.round(items2.reduce((s, i) => s + i.liters, 0) * 100) / 100;
    const weekSet = new Set(items2.map((i) => i.weekKey));
    const seenTags = new Set<string>();
    const tags: string[] = [];
    for (const it of items2) {
      for (const tag of it.data.displayTags) {
        const key = normalizeCompactText(tag.slice(1));
        if (key && !seenTags.has(key) && tags.length < 6) {
          seenTags.add(key);
          tags.push(tag);
        }
      }
    }
    groups.push({
      items: items2,
      targetWeekIndex,
      totalQty,
      totalLiters,
      weekCount: weekSet.size,
      isMerged: weekSet.size === 1,
      isPartlyMerged: weekSet.size > 1 && weekSet.size < items2.length,
      confirmed: items2.every((i) => i.confirmed),
      tags,
    });
  }
  groups.sort(
    (a, b) =>
      a.targetWeekIndex - b.targetWeekIndex ||
      b.items.length - a.items.length ||
      b.totalQty - a.totalQty,
  );
  return {
    groups,
    stats: { minWeek: currentWeek, taggedTotal: tagged.length, futureTagged: future.length, pastTagged: past },
  };
}

/** Порт mergeTargetOptionsForItem: диапазон недель для селекта цели. */
export function mergeTargetRange(
  group: MergeGroup,
  currentWeek: number,
  yearWeekCount: number,
): { min: number; max: number } {
  const weeks = group.items.map((i) => i.weekIndex);
  const minGroup = Math.min(...weeks);
  const maxGroup = Math.max(...weeks);
  return {
    min: Math.max(currentWeek, minGroup - 1),
    max: Math.min(yearWeekCount, maxGroup + 1),
  };
}

export { productionWeekIndex };
